import { create } from 'zustand';
import type { Id } from '@/types/api';

const STORAGE_KEY = 'ole-fleet:company-scope';

interface ScopedCompany {
  id: Id;
  name: string;
}

interface CompanyScopeState {
  /** Null means "my own company" — the only option for a tenant user. */
  scope: ScopedCompany | null;
  select: (company: ScopedCompany | null) => void;
}

function read(): ScopedCompany | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ScopedCompany) : null;
  } catch {
    // Private windows and cleared site data both land here; no scope is a safe
    // default, since it means "my own company".
    return null;
  }
}

/**
 * Which company a platform admin is currently looking at. Every request carries
 * it as X-Company-Id; the backend refuses the header from anyone else, so this
 * is a convenience for the owner rather than a security control.
 *
 * Kept out of the auth store because it survives independently of the session
 * and is meaningless for a tenant user.
 */
export const useCompanyScopeStore = create<CompanyScopeState>((set) => ({
  scope: read(),
  select: (company) => {
    try {
      if (company) localStorage.setItem(STORAGE_KEY, JSON.stringify(company));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Not being able to remember the choice is not a reason to refuse it.
    }
    set({ scope: company });
  },
}));

/** Read outside React — the API client needs this on every request. */
export const currentCompanyScope = (): ScopedCompany | null =>
  useCompanyScopeStore.getState().scope;

export function clearCompanyScope(): void {
  useCompanyScopeStore.getState().select(null);
}
